import React, { useState, useMemo, useCallback, useEffect } from 'react'
import './style.css'
import { PerformanceChart } from '../../types'
import NumberFormat from '../../../../components/DashboardCharts/NumberFormat'
import HoverTooltip from '../../../../../../components/HoverTooltip'

interface PieChartData {
    label: string
    value: number
}

type PerformanceChartProps = {
    contractSum: number
    performanceChart: PerformanceChart
    initialIndex: number
    totalIndex?: number
    theme: string
}

const formatNumber = (num: number | string | null | undefined): string => {
    if (num === null || num === undefined) return '-'
    const parsedNum = typeof num === 'number' ? num : parseFloat(num)
    if (isNaN(parsedNum)) return '-'
    return parsedNum
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        .replace('.', ',')
}

const PieChartPerformanceDoc: React.FC<PerformanceChartProps> = ({
    contractSum,
    performanceChart,
    initialIndex,
    totalIndex,
    theme,
}) => {
    const [pieData, setPieData] = useState<PieChartData[]>([])
    const [selectedLabel, setSelectedLabel] = useState<string>('')
    const [isAnimated, setIsAnimated] = useState<boolean>(false)
    const [isFirstAnimationDone, setIsFirstAnimationDone] =
        useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(true)

    const [infoTooltip, setInfoTooltip] = useState<{
        x: number
        y: number
    } | null>(null)

    const colors =
        theme == 'dark'
            ? ['#3C18BC', '#649A3B', '#5338ed']
            : ['#c7e4fe', '#83d2b7', '#abd7fe']

    useEffect(() => {
        if (performanceChart) {
            const mappedData: PieChartData[] = [
                {
                    label: 'Сумма договора',
                    value: contractSum,
                },
                {
                    label: 'Принято по документам',
                    value: performanceChart.performances_doc_total,
                },
                {
                    label: 'Остаток по документам',
                    value:
                        contractSum - performanceChart.performances_doc_total,
                },
            ]
            setPieData(mappedData)
            setSelectedLabel(
                mappedData[
                    initialIndex >= 0 && initialIndex < mappedData.length
                        ? initialIndex
                        : 0
                ].label
            )

            // Set timeouts after data has been loaded
            setTimeout(() => {
                setIsAnimated(true)
            }, 100)
            setTimeout(() => {
                setIsFirstAnimationDone(true)
            }, 1100)
            setIsLoading(false)
        } else return
    }, [initialIndex, performanceChart])

    const hasTotal =
        typeof totalIndex === 'number' &&
        totalIndex >= 0 &&
        totalIndex < pieData.length
    const validTotalIndex = hasTotal ? totalIndex : null

    const total =
        validTotalIndex !== null
            ? pieData[validTotalIndex].value
            : pieData.reduce((sum, data) => sum + data.value, 0)
    const radius = 15.9
    const circumference = 2 * Math.PI * radius

    const handlePieDataSelection = useCallback(
        (label: string) => {
            if (label !== selectedLabel) {
                setSelectedLabel(label)
            }
        },
        [selectedLabel]
    )

    const isTotalSelected = useMemo(
        () =>
            validTotalIndex !== null &&
            pieData[validTotalIndex]?.label === selectedLabel,
        [selectedLabel, validTotalIndex, pieData]
    )

    let offset = 0
    let delay = 0

    const segments = pieData
        .filter((_, index) => index !== validTotalIndex)
        .map((data, index) => {
            const percentage = (data.value / total) * 100
            const segmentLength = (percentage / 100) * circumference
            const isSelected = data.label === selectedLabel
            const color =
                validTotalIndex !== null
                    ? colors[(index + 1) % colors.length]
                    : colors[index % colors.length]
            const segment = (
                <React.Fragment key={data.label}>
                    <circle
                        cx="18"
                        cy="18"
                        r={radius}
                        fill="transparent"
                        stroke={color}
                        strokeWidth={isSelected ? 4 : 2}
                        strokeDasharray={`${isAnimated ? segmentLength : 0} ${circumference}`}
                        strokeDashoffset={-offset}
                        transform="rotate(0 18 18)"
                        style={{
                            transition: `stroke-dasharray ${Math.round(percentage) / 100}s linear ${delay}s`,
                        }}
                    />
                    {isFirstAnimationDone && (
                        <text
                            x="18"
                            y="20"
                            textAnchor="middle"
                            fontSize="4"
                            fill={
                                isSelected
                                    ? theme == 'dark'
                                        ? '#FFFFFF'
                                        : '#181818'
                                    : 'transparent'
                            }
                            transform="rotate(90 18 18)"
                        >
                            {Math.round(percentage * 100) / 100} %
                        </text>
                    )}
                </React.Fragment>
            )

            delay += Math.round(percentage) / 100
            offset += segmentLength
            return segment
        })

    return (
        <div className="flex flex-col w-full h-full space-y-3">
            <div className="flex flex-row text-[#181818] dark:text-[#FFFFFF] text-[16px]">
                <div className="flex flex-1">
                    Исполнение по документам (руб.)
                </div>
                <div style={{ cursor: 'pointer' }}>
                    <HoverTooltip
                        tooltipContent={
                            <div className="flex flex-col w-[300px] p-2 gap-2 text-justify">
                                <span>
                                    Показатели отражают исполнение договора в
                                    рублях на основании первичных учетных
                                    документов (закрывающих документов) по
                                    договору, подтверждающих исполнение.
                                </span>
                                <span className="font-bold">
                                    Расчет выполняется автоматически и
                                    обновляется в режиме реального времени.
                                </span>
                                <span>
                                    <span className="font-bold">
                                        «Сумма договора»
                                    </span>{' '}
                                    - общая сумма договора в рублях
                                </span>
                                <span>
                                    <span className="font-bold">
                                        «Принято по документам»
                                    </span>{' '}
                                    - сумма в рублях, подтвержденная принятыми
                                    первичными учетными документами
                                    (закрывающими документами) по договору
                                </span>
                                <span>
                                    <span className="font-bold">
                                        «Остаток по документам»
                                    </span>{' '}
                                    - сумма в рублях, оставшаяся к подтверждению
                                    первичными учетными документами
                                    (закрывающими документами) документами;
                                    рассчитывается как разница между «Сумма
                                    договора» и «Принято по документам»
                                </span>
                            </div>
                        }
                        align="left"
                    >
                        <svg
                            className="w-5 h-5"
                            id="info-circle"
                            data-name="Layer 1"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                        >
                            <path
                                fill={theme == 'dark' ? '#FFFFFF' : '#181818'}
                                d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1.25,1.25,0,0,0,12,7.5Z"
                            ></path>
                        </svg>
                    </HoverTooltip>
                </div>
            </div>
            {!performanceChart ? (
                <div className="flex text-[#181818] dark:text-[#FFFFFF] text-[16px] w-full h-full items-center justify-center">
                    Нет данных
                </div>
            ) : (
                <>
                    {isLoading ? (
                        <div className="flex flex-row text-[#181818] dark:text-[#FFFFFF] text-[16px] w-full h-full items-center justify-center gap-2">
                            <svg
                                className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                ></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                ></path>
                            </svg>
                            <span>Загрузка данных</span>
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-row h-full space-x-5 justify-start">
                                <div className="flex items-center">
                                    <svg
                                        viewBox="0 0 36 36"
                                        className="pie-chart"
                                        aria-label="Диаграмма закупок"
                                        aria-live="polite"
                                    >
                                        {segments}
                                        {isTotalSelected &&
                                            validTotalIndex !== null && (
                                                <React.Fragment key="total-segment">
                                                    <circle
                                                        r="15.9"
                                                        cx="18"
                                                        cy="18"
                                                        fill="transparent"
                                                        stroke={
                                                            colors[
                                                                validTotalIndex %
                                                                    colors.length
                                                            ]
                                                        }
                                                        strokeWidth={4}
                                                        strokeDasharray={`100 0`}
                                                        strokeDashoffset={0}
                                                        style={{
                                                            transition:
                                                                'stroke-dasharray 0.5s ease, stroke 0.5s ease',
                                                        }}
                                                    />
                                                    {isFirstAnimationDone && (
                                                        <text
                                                            x="18"
                                                            y="20"
                                                            textAnchor="middle"
                                                            fontSize="4"
                                                            fill={
                                                                theme == 'dark'
                                                                    ? '#FFFFFF'
                                                                    : '#181818'
                                                            }
                                                            transform="rotate(90 18 18)"
                                                        >
                                                            100 %
                                                        </text>
                                                    )}
                                                </React.Fragment>
                                            )}
                                    </svg>
                                </div>
                                <div className="flex items-center">
                                    <ul className="flex flex-col items-start space-y-1">
                                        {pieData.map((item, index) => (
                                            <li
                                                key={item.label}
                                                style={{
                                                    color:
                                                        selectedLabel ===
                                                        item.label
                                                            ? theme == 'dark'
                                                                ? '#FFFFFF'
                                                                : '#181818'
                                                            : theme == 'dark'
                                                              ? '#8A8A8A'
                                                              : '#6A6A6A',
                                                }}
                                                className="flex flex-row items-center space-x-2 text-[12.8px]"
                                            >
                                                <svg width="24" height="24">
                                                    {selectedLabel ===
                                                        item.label && (
                                                        <circle
                                                            cx="12"
                                                            cy="12"
                                                            r="8"
                                                            fill="none"
                                                            stroke={
                                                                colors[
                                                                    index %
                                                                        colors.length
                                                                ] + '55'
                                                            }
                                                            strokeWidth="5"
                                                        />
                                                    )}
                                                    <circle
                                                        cx="12"
                                                        cy="12"
                                                        r="6"
                                                        fill={
                                                            colors[
                                                                index %
                                                                    colors.length
                                                            ]
                                                        }
                                                    />
                                                </svg>
                                                <button
                                                    aria-label={`Выбрать ${item.label}`}
                                                    onClick={() =>
                                                        handlePieDataSelection(
                                                            item.label
                                                        )
                                                    }
                                                >
                                                    <div className="text-left">
                                                        {item.label}
                                                    </div>
                                                    <div className="text-left">
                                                        <NumberFormat
                                                            value={item.value}
                                                        />
                                                    </div>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}
            {/* {infoTooltip && (
                <div
                    className="flex flex-col w-[300px] p-2 gap-2 text-justify"
                    style={{
                        position: 'fixed',
                        left: infoTooltip.x - 300,
                        top: infoTooltip.y - 200,
                        backgroundColor:
                            theme == 'dark' ? '#464447' : '#ebebeb',
                        border: '1px solid gray',
                        borderRadius: '5px',
                        color: theme == 'dark' ? '#FFFFFF' : '#181818',
                        fontSize: 9,
                    }}
                >
                    <span>
                        <span className="font-bold">«Всего закупок»</span> - это
                        сумма показателей значений «Закупки текущего года»,
                        «Закупки прошлых годов», «Закупки будущих годов» и ровна
                        количеству строк позиций плана закупки
                    </span>
                    <span>
                        <span className="font-bold">
                            «Закупки текущего года»
                        </span>{' '}
                        отражает количество закупочных процедур, размещение
                        которых запланировано на текущий год
                    </span>
                    <span>
                        <span className="font-bold">
                            «Закупки прошлых годов»
                        </span>{' '}
                        отражает количество закупочных процедур прошлых годов,
                        которые перешли в текущий план, и оплата которых
                        приходится на этот и/либо последующие периоды
                    </span>
                    <span>
                        <span className="font-bold">
                            «Закупки будущих годов»
                        </span>{' '}
                        отражает количество закупок, размещение которых
                        запланировано на будущие года
                    </span>
                </div>
            )} */}
        </div>
    )
}

export default PieChartPerformanceDoc
